import { InterpolationBuffer, RenderClock, type Snapshot } from '@battle/netcode';
import {
  type CreatureState,
  createFixedStep,
  type Input,
  NEUTRAL_INPUT,
  type SimEvent,
} from '@battle/sim';
import type * as THREE from 'three/webgpu';
import { createFollowCamera } from '../camera/follow-camera.ts';
import { loadCreature } from '../creatures/creature-model.ts';
import { type CreatureView, createCreatureView } from '../creatures/creature-view.ts';
import { createInput } from '../input/input.ts';
import { connect, parseNetConditions } from '../net/connection.ts';
import { type Game, groundProbe, setupArena } from './game.ts';

/**
 * Online play (docs/05, M2): the server simulates everyone. This client predicts its own
 * creature with the same sim, reconciles on every snapshot (restore at the server tick, replay
 * unacknowledged inputs, smooth the visual error), and renders the other creature interpolated
 * ~100 ms in the past.
 */
/** Base remote-render delay: two snapshot intervals; RenderClock adds the observed jitter. */
const INTERP_DELAY_TICKS = 4;
const SNAP_ERROR_M = 2;

export async function createOnlineGame(opts: {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGPURenderer;
  canvas: HTMLElement;
  params: URLSearchParams;
}): Promise<Game> {
  const { scene, camera, renderer, canvas, params } = opts;
  const creatureId = params.get('creature') === 'rillwing' ? 'rillwing' : 'kilnback';
  const room = (params.get('room') ?? '').toLowerCase();

  const { world } = await setupArena(scene, params);
  const conn = connect({
    gs: params.get('gs') ?? 'gs-1',
    room,
    creature: creatureId,
    name: params.get('name') ?? 'player',
    conditions: parseNetConditions(params.get('net')),
    onWelcome: (w) => {
      // Put the room code in the URL, so a reload or a shared link joins the same room.
      const url = new URL(location.href);
      url.searchParams.set('room', w.room);
      history.replaceState(null, '', url);
    },
  });

  const input = createInput(canvas);
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const cam = createFollowCamera(camera, world.castRay, reduceMotion);
  cam.scale = creatureId === 'kilnback' ? 1 : 1.15;
  const ground = groundProbe(world);

  const views = new Map<number, CreatureView>();
  const loading = new Set<number>();
  const interp = new InterpolationBuffer(6);
  const renderClock = new RenderClock();
  const remoteSmooth = new Map<
    number,
    { x: number; y: number; z: number; ox: number; oy: number; oz: number }
  >();
  const inbox: { snap: Snapshot; at: number }[] = [];
  conn.onSnapshot = (snap, at) => inbox.push({ snap, at });

  const fixed = createFixedStep();
  let clientTick = 0;
  let started = false;
  let dilation = 1;
  let resyncFrom = 0;
  let resyncs = 0;
  let lastSnapTick = -1;
  const fixLog: object[] = [];
  let prevOwn: CreatureState | null = null;
  let currOwn: CreatureState | null = null;
  const errorOffset = { x: 0, y: 0, z: 0 };
  const netStats = {
    corrections: 0,
    correctionSum: 0,
    snaps: 0,
    lead: 0,
    tickMs: 0,
    windowStart: performance.now(),
    perSec: 0,
    avgCm: 0,
  };
  let lockOn = false;
  let otherHandle: number | null = null;

  const own = () => (conn.welcome ? world.creatures[conn.welcome.handle] : undefined);

  /** Make the local world mirror the server's creature list (same handles, same order). */
  const ensureCreatures = (creatures: CreatureState[]) => {
    for (const c of [...creatures].sort((a, b) => a.handle - b.handle)) {
      while (world.creatures.length <= c.handle) {
        const s = creatures.find((x) => x.handle === world.creatures.length) ?? c;
        world.addCreature(s.creatureId, { x: s.pos.x, y: s.pos.y, z: s.pos.z, yaw: s.yaw });
      }
      if (!views.has(c.handle) && !loading.has(c.handle)) {
        loading.add(c.handle);
        loadCreature(renderer, c.creatureId).then((model) => {
          scene.add(model.root);
          views.set(
            c.handle,
            createCreatureView(model, c.creatureId, world.tuningOf(c.handle).dodge.ticks),
          );
        });
      }
    }
  };

  const route = (events: SimEvent[]) => {
    for (const e of events) {
      views.get(e.handle)?.onEvent(e);
      if (e.handle !== conn.welcome?.handle) continue;
      if (e.type === 'land' && e.impact > 9) cam.addTrauma(Math.min(0.5, (e.impact - 9) / 12));
      if (e.type === 'dodge') cam.addTrauma(0.08);
    }
  };

  const reconcile = (snap: Snapshot, at: number) => {
    const handle = conn.welcome?.handle;
    if (handle === undefined) return;
    // A stale snapshot would rewind to a state whose inputs were already acknowledged.
    if (snap.serverTick <= lastSnapTick) return;
    lastSnapTick = snap.serverTick;
    ensureCreatures(snap.creatures);
    interp.push(snap.serverTick, snap.creatures);
    renderClock.observe(snap.serverTick, at);
    conn.clock.noteLead(snap.inputLead, at);
    netStats.lead = snap.inputLead;
    netStats.tickMs = snap.tickMs;

    if (!started) {
      // First snapshot: start the local clock a little ahead of the server.
      clientTick =
        snap.serverTick + conn.clock.targetLead() + Math.ceil(conn.clock.rttMs / 2 / (1000 / 60));
      started = true;
    }
    // Lead feedback describes inputs sent ~RTT ago. After a hard jump, ignore it until the
    // snapshots reflect inputs stamped after the jump, or the correction repeats and overshoots
    // (e.g. after a tab stall).
    if (snap.lastInput >= resyncFrom) {
      const d = conn.clock.dilation(snap.inputLead);
      if (d === 0) {
        clientTick += conn.clock.targetLead() - snap.inputLead;
        resyncFrom = clientTick;
        resyncs++;
        dilation = 1;
      } else {
        dilation = d;
      }
    }

    // Remote creatures: keep their colliders near the server's positions for local collision.
    for (const c of snap.creatures) if (c.handle !== handle) world.setState(c.handle, c);

    const mine = snap.creatures.find((c) => c.handle === handle);
    if (!mine) return;
    const before = own() ? { ...(own() as CreatureState).pos } : null;
    world.setState(handle, mine);
    conn.pending.ack(snap.serverTick);
    for (const p of conn.pending.all) {
      if (p.tick > clientTick) break;
      world.step(new Map([[handle, p]])); // replayed events were already presented
    }
    const after = (own() as CreatureState).pos;
    if (before) {
      const ex = before.x - after.x;
      const ey = before.y - after.y;
      const ez = before.z - after.z;
      const err = Math.hypot(ex, ey, ez);
      if (err > 0.001) {
        netStats.corrections++;
        netStats.correctionSum += err;
        if (err > 0.05) {
          const ticks = conn.pending.all.map((p) => p.tick);
          const gaps = ticks.filter((t, i) => i > 0 && t !== (ticks[i - 1] as number) + 1).length;
          fixLog.push({
            err: +err.toFixed(3),
            S: snap.serverTick,
            clientTick,
            pending: ticks.length,
            gaps,
            first: ticks[0],
            lead: snap.inputLead,
            resyncs,
          });
          if (fixLog.length > 50) fixLog.shift();
        }
      }
      if (err > SNAP_ERROR_M) {
        errorOffset.x = errorOffset.y = errorOffset.z = 0;
      } else {
        errorOffset.x += ex;
        errorOffset.y += ey;
        errorOffset.z += ez;
      }
    }
    currOwn = structuredClone(own()) as CreatureState;
    if (prevOwn && before) {
      // Keep the render-interpolation pair in the corrected frame; errorOffset covers the jump.
      prevOwn = {
        ...prevOwn,
        pos: {
          x: prevOwn.pos.x - (before.x - after.x),
          y: prevOwn.pos.y - (before.y - after.y),
          z: prevOwn.pos.z - (before.z - after.z),
        },
      };
    }
    prevOwn ??= currOwn;
  };

  window.__battleDebug = {
    world,
    camera,
    scene,
    setCameraYaw(yaw: number) {
      cam.yaw = yaw;
    },
  };
  (window as unknown as { __battleNet?: unknown }).__battleNet = {
    conn,
    netStats,
    fixLog,
    get clientTick() {
      return clientTick;
    },
  };

  return {
    online: true,
    tuning: {
      movement: () => world.tuningOf(0),
      setMovement: () => {},
      camera: cam.tuning,
      input: input.settings,
      view: {
        walkRef: 1.7,
        runRef: 5.2,
        leanPerYawRate: 0.05,
        leanMax: 0.2,
        pitchPerAccel: 0.006,
        squash: 0.12,
        dodgeRoll: true,
        rollPivot: 0.6,
      },
      resetPlayer: () => {},
    },
    status: () => {
      const me = own();
      const now = performance.now();
      if (now - netStats.windowStart > 1000) {
        netStats.perSec = netStats.corrections;
        netStats.avgCm = netStats.corrections
          ? (netStats.correctionSum / netStats.corrections) * 100
          : 0;
        netStats.corrections = netStats.correctionSum = 0;
        netStats.windowStart = now;
      }
      const s = conn.stats;
      return {
        room: conn.welcome?.room ?? (conn.error ? `error: ${conn.error}` : 'connecting…'),
        net: `rtt ${s.rttMs.toFixed(0)} ms ±${s.jitterMs.toFixed(0)} · lead ${netStats.lead} · buf ${interp.depth} · interp ${renderClock.delayTicks}t · fix ${netStats.perSec}/s ${netStats.avgCm.toFixed(1)} cm · srv ${netStats.tickMs.toFixed(2)} ms${resyncs ? ` · resync ${resyncs}` : ''}`,
        creature: creatureId,
        mode: me ? (me.sprinting ? 'sprint' : me.mode) : '—',
        speed: me ? Math.hypot(me.vel.x, me.vel.z) : 0,
        stamina: me?.stamina ?? 100,
        staminaMax: 100,
        locked: input.locked,
        device: input.device,
        lockOn,
      };
    },
    update(dt, elapsed) {
      for (const { snap, at } of inbox.splice(0)) reconcile(snap, at);
      const handle = conn.welcome?.handle;
      const me = own();
      const others = world.creatures.filter((c) => c.handle !== handle);
      otherHandle = others[0]?.handle ?? null;

      if (input.consumePress('lockOn')) lockOn = !lockOn && otherHandle !== null;
      cam.lockTarget =
        lockOn && otherHandle !== null
          ? () => (world.creatures[otherHandle as number] as CreatureState).pos
          : null;
      const look = input.takeLook(dt);

      if (started && handle !== undefined && me) {
        const steps = fixed.advance(dt * dilation);
        for (let i = 0; i < steps; i++) {
          clientTick++;
          const sampled: Input = input.sample(cam.yaw, cam.pitch);
          conn.sendInput({ ...sampled, tick: clientTick });
          prevOwn = currOwn ?? structuredClone(me);
          const inputs = new Map<number, Input>([[handle, sampled]]);
          for (const o of others) inputs.set(o.handle, NEUTRAL_INPUT);
          route(world.step(inputs).filter((e) => e.handle === handle));
          currOwn = structuredClone(own()) as CreatureState;
        }
      }
      input.endFrame();

      // Visual error smoothing: the rendered position eases from where it was to the corrected
      // prediction over ~100 ms instead of popping.
      const decay = Math.exp(-dt / 0.1);
      errorOffset.x *= decay;
      errorOffset.y *= decay;
      errorOffset.z *= decay;

      // Remote creatures render in the past, between buffered snapshots.
      const renderTick = renderClock.advance(performance.now(), dt * 1000, INTERP_DELAY_TICKS);
      for (const [h, view] of views) {
        if (h === handle) {
          if (!prevOwn || !currOwn) continue;
          const shift = (s: CreatureState): CreatureState => ({
            ...s,
            pos: {
              x: s.pos.x + errorOffset.x,
              y: s.pos.y + errorOffset.y,
              z: s.pos.z + errorOffset.z,
            },
          });
          const look =
            lockOn && otherHandle !== null ? null : { yaw: cam.yaw, pitch: cam.pitch * 0.6 };
          view.sync(shift(prevOwn), shift(currOwn), fixed.alpha, dt, elapsed, { look, ground });
        } else {
          const sampled = interp.sample(h, renderTick);
          if (!sampled) continue;
          // Hide the snap when extrapolation (after a snapshot stall) is corrected: carry the
          // difference as an offset that fades over ~120 ms, like the local creature's error.
          const r = remoteSmooth.get(h) ?? {
            x: sampled.pos.x,
            y: sampled.pos.y,
            z: sampled.pos.z,
            ox: 0,
            oy: 0,
            oz: 0,
          };
          const px = r.x + r.ox;
          const py = r.y + r.oy;
          const pz = r.z + r.oz;
          const ex = px + sampled.vel.x * dt - sampled.pos.x;
          const ez = pz + sampled.vel.z * dt - sampled.pos.z;
          const fade = Math.exp(-dt / 0.12);
          if (Math.hypot(ex, ez) > 0.12 && Math.hypot(ex, ez) < SNAP_ERROR_M) {
            r.ox = px - sampled.pos.x;
            r.oy = py - sampled.pos.y;
            r.oz = pz - sampled.pos.z;
          }
          r.ox *= fade;
          r.oy *= fade;
          r.oz *= fade;
          r.x = sampled.pos.x;
          r.y = sampled.pos.y;
          r.z = sampled.pos.z;
          remoteSmooth.set(h, r);
          const s = { ...sampled, pos: { x: r.x + r.ox, y: r.y + r.oy, z: r.z + r.oz } };
          const target = handle !== undefined ? world.creatures[handle] : undefined;
          const look = target
            ? { yaw: Math.atan2(target.pos.x - s.pos.x, target.pos.z - s.pos.z), pitch: 0 }
            : null;
          view.sync(s, s, 0, dt, elapsed, { look, ground });
        }
      }
      const p = handle !== undefined ? views.get(handle)?.model.root.position : undefined;
      if (p) cam.update(dt, { x: p.x, y: p.y, z: p.z }, { sprinting: !!me?.sprinting, look });
    },
  };
}
